import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import i18n from '../i18n/config';
import { API_ENDPOINTS } from '../config.js';
import Navbar from './Navbar';
import TrialNotification from './TrialNotification';
import { validatePassword, checkPasswordRequirements } from '../utils/passwordValidation';
import logger from '../utils/logger';

export default function Profile() {
  const { t } = useTranslation();
  const { isDark, toggleDarkMode } = useTheme();
  const colors = getThemeColors(isDark);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scriptMode, setScriptMode] = useState('latin');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [languageLoading, setLanguageLoading] = useState(false);
  
  // User form state
  const [userForm, setUserForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Company form state
  const [companyForm, setCompanyForm] = useState({
    name: '',
    pib: '',
    mb: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    email: '',
    website: ''
  });
  const [companyFormLoading, setCompanyFormLoading] = useState(false);

  const navigate = useNavigate();

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        // Load user profile data (using cookie-based auth)
        const response = await fetch(API_ENDPOINTS.PROFILE, {
          method: 'GET',
          credentials: 'include', // Include cookies
        });

        if (!response.ok) {
          if (response.status === 401) {
            navigate('/login');
            return;
          }
          throw new Error('Failed to load profile');
        }
        
        // Extract CSRF token from response headers
        const csrfToken = response.headers.get('X-CSRF-Token');
        if (csrfToken) {
          import('../utils/csrf').then(({ extractCSRFToken }) => {
            extractCSRFToken(response);
          });
        }

        const data = await response.json();
        setUserProfile(data.user);
        setCompanyProfile(data.company);
        
        // Set language from user profile
        if (data.user?.language) {
          setSelectedLanguage(data.user.language);
          i18n.changeLanguage(data.user.language);
          localStorage.setItem('i18nextLng', data.user.language);
        }

        // Load user info for role checking
        const userResponse = await fetch(API_ENDPOINTS.PROFILE_USER, {
          method: 'GET',
          credentials: 'include', // Include cookies
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUserInfo(userData.user);
        }
        
        // No need to initialize user form with email since it's read-only

        setCompanyForm({
          name: data.company?.name || '',
          pib: data.company?.pib || '',
          mb: data.company?.mb || '',
          address: data.company?.address || '',
          city: data.company?.city || '',
          postalCode: data.company?.postalCode || '',
          phone: data.company?.phone || '',
          email: data.company?.email || '',
          website: data.company?.website || ''
        });

        setLoading(false);
      } catch (err) {
        logger.error('Error loading profile:', err);
        setError(t('profile.loadError'));
        setLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleUserFormSubmit = async (e) => {
    e.preventDefault();
    setUserFormLoading(true);
    setError('');
    setSuccess('');

    // Validate password confirmation
    if (userForm.newPassword && userForm.newPassword !== userForm.confirmPassword) {
      setError(t('profile.passwordMismatch'));
      setUserFormLoading(false);
      return;
    }

    // Validate password strength
    if (userForm.newPassword) {
      const passwordValidation = validatePassword(userForm.newPassword);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.errors.join(' '));
        setUserFormLoading(false);
        return;
      }
    }

    try {
      const requestBody = {};

      // Only include password fields if new password is provided
      if (userForm.newPassword) {
        requestBody.currentPassword = userForm.currentPassword;
        requestBody.newPassword = userForm.newPassword;
      } else {
        // If no password change, don't send the request
        setError(t('profile.noChanges'));
        setUserFormLoading(false);
        return;
      }

      // Use authenticatedFetch to include CSRF token
      const { authenticatedFetch } = await import('../utils/api');
      const response = await authenticatedFetch(API_ENDPOINTS.PROFILE_USER, {
        method: 'PUT',
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('profile.updateError'));
      }

      setSuccess(t('profile.profileUpdated'));
      setUserProfile(data.user);
      
      // Update language if it changed
      if (data.user?.language && data.user.language !== selectedLanguage) {
        setSelectedLanguage(data.user.language);
        i18n.changeLanguage(data.user.language);
        localStorage.setItem('i18nextLng', data.user.language);
      }
      
      // Clear password fields
      setUserForm(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

    } catch (err) {
      setError(err.message);
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleCompanyFormSubmit = async (e) => {
    e.preventDefault();
    setCompanyFormLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use authenticatedFetch to include CSRF token
      const { authenticatedFetch } = await import('../utils/api');
      const response = await authenticatedFetch(API_ENDPOINTS.PROFILE_COMPANY, {
        method: 'PUT',
        body: JSON.stringify(companyForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('profile.updateError'));
      }

      setSuccess(t('profile.companyUpdated'));
      setCompanyProfile(data.company);

    } catch (err) {
      setError(err.message);
    } finally {
      setCompanyFormLoading(false);
    }
  };

  const handleLanguageChange = async (e) => {
    const newLanguage = e.target.value;
    setLanguageLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use authenticatedFetch to include CSRF token
      const { authenticatedFetch } = await import('../utils/api');
      const response = await authenticatedFetch(API_ENDPOINTS.PROFILE_USER, {
        method: 'PUT',
        body: JSON.stringify({ language: newLanguage })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('profile.updateError'));
      }

      setSelectedLanguage(newLanguage);
      i18n.changeLanguage(newLanguage);
      localStorage.setItem('i18nextLng', newLanguage);
      setSuccess(t('profile.languageUpdated'));
      
      if (data.user) {
        setUserProfile(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLanguageLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: colors.backgroundLight, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease' }}>
        <div style={{ fontSize: 18, color: colors.primary }}>{t('profile.loadingProfile')}</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.backgroundLight, transition: 'background-color 0.3s ease' }}>
      <TrialNotification companyData={companyProfile} />
      <Navbar scriptMode={scriptMode} onScriptModeChange={setScriptMode} />
      
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: colors.backgroundHover,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: colors.text,
              marginBottom: '1rem',
              transition: 'background-color 0.2s, border-color 0.2s, color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = colors.border}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.backgroundHover}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z"/>
            </svg>
            {t('profile.backToDashboard')}
          </button>
          
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: colors.text, margin: 0, transition: 'color 0.3s ease' }}>
            {t('profile.title')}
          </h1>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            background: isDark ? '#7f1d1d' : '#fef2f2',
            border: `1px solid ${colors.error}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '1rem',
            color: colors.error,
            transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: isDark ? '#14532d' : '#f0fdf4',
            border: `1px solid ${colors.success}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '1rem',
            color: colors.success,
            transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}

        <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: '1fr 1fr' }}>
          {/* User Profile Form */}
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: `0 1px 3px ${colors.shadow}`,
            transition: 'background-color 0.3s ease, box-shadow 0.3s ease'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.primary, marginBottom: '1rem', transition: 'color 0.3s ease' }}>
              {t('profile.userProfile')}
            </h2>

            {/* Language Selector */}
            <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${colors.border}` }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '8px' }}>
                {t('profile.language')}
              </label>
              <select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                disabled={languageLoading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: languageLoading ? colors.backgroundHover : colors.background,
                  color: colors.text,
                  cursor: languageLoading ? 'not-allowed' : 'pointer',
                  transition: 'border-color 0.2s, background-color 0.2s, color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = colors.primary}
                onBlur={(e) => e.target.style.borderColor = colors.border}
              >
                <option value="en">{t('languages.en')}</option>
                <option value="sr">{t('languages.sr')}</option>
                <option value="zh">{t('languages.zh')}</option>
              </select>
            </div>

            {/* Dark Mode Toggle */}
            <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${colors.border}` }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '8px' }}>
                {t('userMenu.darkMode')} / {t('userMenu.lightMode')}
              </label>
                  <button
                    onClick={toggleDarkMode}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: colors.background,
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'border-color 0.2s, background-color 0.2s, color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.backgroundHover}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.background}
              >
                {isDark ? (
                  <>
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
                    </svg>
                    {t('userMenu.lightMode')}
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd"/>
                    </svg>
                    {t('userMenu.darkMode')}
                  </>
                )}
              </button>
            </div>

            {/* Display read-only email */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
                {t('profile.emailReadOnly')}
              </label>
              <input
                type="email"
                value={userProfile?.email || ''}
                readOnly
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: colors.backgroundHover,
                  color: colors.textSecondary,
                  transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
                  cursor: 'not-allowed',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              />
            </div>

            <form onSubmit={handleUserFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem', transition: 'border-color 0.3s ease' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: colors.text, marginBottom: '1rem', transition: 'color 0.3s ease' }}>
                  {t('profile.changePassword')}
                </h3>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('auth.currentPassword')}
                  </label>
                  <input
                    type="password"
                    value={userForm.currentPassword}
                    onChange={(e) => setUserForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: colors.background,
                      color: colors.text,
                      transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('auth.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={userForm.newPassword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserForm(prev => ({ ...prev, newPassword: value }));
                      setShowPasswordRequirements(true);
                      const requirements = checkPasswordRequirements(value);
                      setPasswordRequirements(requirements);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: colors.background,
                      color: colors.text,
                      transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      setShowPasswordRequirements(true);
                    }}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                    required
                  />
                  {showPasswordRequirements && userForm.newPassword && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: colors.textSecondary,
                      background: colors.backgroundHover,
                      padding: '12px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '6px', color: colors.text, transition: 'color 0.3s ease' }}>{t('profile.passwordRequirements')}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ color: passwordRequirements?.length ? colors.success : colors.error }}>
                          {passwordRequirements?.length ? '✓' : '✗'} {t('profile.minLength')}
                        </div>
                        <div style={{ color: passwordRequirements?.uppercase ? colors.success : colors.error }}>
                          {passwordRequirements?.uppercase ? '✓' : '✗'} {t('profile.uppercase')}
                        </div>
                        <div style={{ color: passwordRequirements?.lowercase ? colors.success : colors.error }}>
                          {passwordRequirements?.lowercase ? '✓' : '✗'} {t('profile.lowercase')}
                        </div>
                        <div style={{ color: passwordRequirements?.number ? colors.success : colors.error }}>
                          {passwordRequirements?.number ? '✓' : '✗'} {t('profile.number')}
                        </div>
                        <div style={{ color: passwordRequirements?.special ? colors.success : colors.error }}>
                          {passwordRequirements?.special ? '✓' : '✗'} {t('profile.special')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('auth.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    value={userForm.confirmPassword}
                    onChange={(e) => setUserForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: colors.background,
                      color: colors.text,
                      transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={userFormLoading}
                style={{
                  background: userFormLoading ? colors.textLight : colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: userFormLoading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!userFormLoading) e.target.style.background = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  if (!userFormLoading) e.target.style.background = colors.primary;
                }}
              >
                {userFormLoading ? t('profile.updating') : t('profile.changePassword')}
              </button>
            </form>
          </div>

          {/* Company Profile Form */}
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: `0 1px 3px ${colors.shadow}`,
            transition: 'background-color 0.3s ease, box-shadow 0.3s ease'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.primary, marginBottom: '1rem', transition: 'color 0.3s ease' }}>
              {t('profile.companyProfile')}
            </h2>

            {/* Check if user is admin */}
            {userInfo && userInfo.role !== 'admin' ? (
              // Read-only view for non-admins
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{
                  background: colors.backgroundHover,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  transition: 'background-color 0.3s ease, border-color 0.3s ease'
                }}>
                  <p style={{ color: colors.textSecondary, fontSize: '14px', margin: 0, transition: 'color 0.3s ease' }}>
                    {t('company.onlyAdminCanEdit')}
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('company.name')}
                  </label>
                  <input
                    type="text"
                    value={companyForm.name}
                    readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                      {t('company.pib')}
                    </label>
                    <input
                      type="text"
                      value={companyForm.pib}
                      readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                      {t('company.mb')}
                    </label>
                    <input
                      type="text"
                      value={companyForm.mb}
                      readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('common.address')}
                  </label>
                  <input
                    type="text"
                    value={companyForm.address}
                    readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                      {t('common.city')}
                    </label>
                    <input
                      type="text"
                      value={companyForm.city}
                      readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                      {t('company.postalCode')}
                    </label>
                    <input
                      type="text"
                      value={companyForm.postalCode}
                      readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('common.phone')}
                  </label>
                  <input
                    type="text"
                    value={companyForm.phone}
                    readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('common.email')}
                  </label>
                  <input
                    type="email"
                    value={companyForm.email}
                    readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('company.website')}
                  </label>
                  <input
                    type="url"
                    value={companyForm.website}
                    readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  />
                </div>
              </div>
            ) : (
              // Editable form for admins
              <form onSubmit={handleCompanyFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                  {t('company.name')}
                </label>
                <input
                  type="text"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: colors.background,
                    color: colors.text,
                    transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('company.pibReadOnly')}
                  </label>
                  <input
                    type="text"
                    value={companyForm.pib}
                    readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('company.mbReadOnly')}
                  </label>
                  <input
                    type="text"
                    value={companyForm.mb}
                    readOnly
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: colors.backgroundHover,
                    color: colors.textSecondary,
                    cursor: 'not-allowed',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                  {t('common.address')}
                </label>
                <input
                  type="text"
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, address: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: colors.background,
                    color: colors.text,
                    transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('common.city')}
                  </label>
                  <input
                    type="text"
                    value={companyForm.city}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, city: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: colors.background,
                      color: colors.text,
                      transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                    {t('company.postalCode')}
                  </label>
                  <input
                    type="text"
                    value={companyForm.postalCode}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, postalCode: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: colors.background,
                      color: colors.text,
                      transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                  {t('common.phone')}
                </label>
                <input
                  type="tel"
                  value={companyForm.phone}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, phone: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: colors.background,
                    color: colors.text,
                    transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                  {t('company.companyEmail')}
                </label>
                <input
                  type="email"
                  value={companyForm.email}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: colors.background,
                    color: colors.text,
                    transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '4px', transition: 'color 0.3s ease' }}>
                  {t('company.website')}
                </label>
                <input
                  type="url"
                  value={companyForm.website}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: colors.background,
                    color: colors.text,
                    transition: 'border-color 0.2s, background-color 0.3s ease, color 0.3s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>

              <button
                type="submit"
                disabled={companyFormLoading}
                style={{
                  background: companyFormLoading ? colors.textLight : colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: companyFormLoading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!companyFormLoading) e.target.style.background = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  if (!companyFormLoading) e.target.style.background = colors.primary;
                }}
              >
                {companyFormLoading ? t('profile.updating') : t('profile.updateCompany')}
              </button>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 